import { useEffect, useState } from "react";
import { Plus, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { mediaUrl } from "@/lib/api";
import { Field, Input, Textarea, Select, Btn, Toggle, Modal } from "@/components/admin/AdminUI";
import ImageUpload from "@/components/admin/ImageUpload";

const POSITIONS = ["Header Banner", "Billboard", "In-Article", "Sidebar", "Between Articles", "Categorías", "Footer Banner"];
const EMPTY = { name: "", position: "Billboard", ad_type: "image", image: "", url: "", embed_code: "", active: true, start_date: "", end_date: "" };

export default function AdsAdmin() {
  const [ads, setAds] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/ads").then((r) => setAds(r.data || []));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...EMPTY, ...a }); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name) { toast.error("El nombre es obligatorio"); return; }
    if (form.ad_type === "embed" && !form.embed_code.trim()) { toast.error("Pega el código del anuncio"); return; }
    const payload = { ...form, start_date: form.start_date || null, end_date: form.end_date || null };
    try {
      if (editing) await api.put(`/admin/ads/${editing.id}`, payload);
      else await api.post("/admin/ads", payload);
      toast.success("Anuncio guardado"); setOpen(false); load();
    } catch { toast.error("Error al guardar"); }
  };
  const del = async (id) => { if (!window.confirm("¿Eliminar anuncio?")) return; await api.delete(`/admin/ads/${id}`); load(); };

  return (
    <div>
      <header className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-4xl tracking-tight">Anuncios</h1>
          <p className="text-sunset-muted text-sm mt-1">Gestiona los espacios publicitarios del sitio.</p>
        </div>
        <Btn variant="accent" onClick={openNew} data-testid="new-ad-btn"><Plus size={15} /> Nuevo anuncio</Btn>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {ads.map((a) => (
          <div key={a.id} className="bg-white border border-sunset-border p-5" data-testid={`ad-card-${a.id}`}>
            <div className="aspect-video bg-sunset-border overflow-hidden mb-3 flex items-center justify-center">
              {a.ad_type === "embed" ? (
                <span className="text-xs uppercase tracking-widest text-sunset-muted px-4 text-center">Código embebido<br />(red de anuncios)</span>
              ) : (
                a.image && <img src={mediaUrl(a.image)} alt="" className="w-full h-full object-cover" />
              )}
            </div>
            <p className="font-medium">{a.name}</p>
            <p className="text-xs text-sunset-accent uppercase tracking-wider mb-2">{a.position} · {a.ad_type === "embed" ? "Embed" : "Imagen"}</p>
            <div className="flex items-center justify-between">
              <span className={`text-xs ${a.active ? "text-green-700" : "text-sunset-muted"}`}>{a.active ? "Activo" : "Inactivo"}</span>
              <div className="flex gap-2">
                <button onClick={() => openEdit(a)} className="p-2 hover:text-sunset-accent"><Edit size={16} /></button>
                <button onClick={() => del(a.id)} className="p-2 hover:text-red-600"><Trash2 size={16} /></button>
              </div>
            </div>
          </div>
        ))}
        {ads.length === 0 && <p className="col-span-full text-center text-sunset-muted py-16">No hay anuncios configurados.</p>}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Editar anuncio" : "Nuevo anuncio"}>
        <Field label="Nombre"><Input data-testid="ad-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></Field>
        <Field label="Posición"><Select value={form.position} onChange={(e) => set("position", e.target.value)}>{POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}</Select></Field>
        <Field label="Tipo de anuncio">
          <Select data-testid="ad-type" value={form.ad_type} onChange={(e) => set("ad_type", e.target.value)}>
            <option value="image">Imagen propia (con enlace)</option>
            <option value="embed">Código embebido (Google AdSense, Amazon, etc.)</option>
          </Select>
        </Field>
        {form.ad_type === "embed" ? (
          <Field label="Código del anuncio (HTML / JavaScript)" hint="Pega el código que te da la red de anuncios. Se ejecutará solo en tu dominio de producción desplegado.">
            <Textarea data-testid="ad-embed-code" rows={6} value={form.embed_code} onChange={(e) => set("embed_code", e.target.value)} placeholder="<script async src=&quot;https://…&quot;></script>&#10;<ins class=&quot;adsbygoogle&quot;…></ins>" />
          </Field>
        ) : (
          <>
            <div className="mb-4"><ImageUpload label="Imagen" value={form.image} onChange={(v) => set("image", v)} /></div>
            <Field label="URL de destino"><Input value={form.url} onChange={(e) => set("url", e.target.value)} placeholder="https://…" /></Field>
          </>
        )}
        <div className="grid grid-cols-2 gap-4">
          <Field label="Fecha inicio"><Input type="date" value={(form.start_date || "").slice(0, 10)} onChange={(e) => set("start_date", e.target.value)} /></Field>
          <Field label="Fecha fin"><Input type="date" value={(form.end_date || "").slice(0, 10)} onChange={(e) => set("end_date", e.target.value)} /></Field>
        </div>
        <div className="py-2 mb-4"><Toggle checked={form.active} onChange={(v) => set("active", v)} label="Activo" /></div>
        <Btn variant="accent" onClick={save} data-testid="save-ad-btn">Guardar</Btn>
      </Modal>
    </div>
  );
}
